import { useEffect, useState } from "react";
import { EnvelopeSimple, Phone } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { fmtDate, kycBadge, riskBadge } from "../../lib/format";
import AppLayout, { PageLoader } from "../../components/AppLayout";
import SecureAvatar from "../../components/SecureAvatar";
import { Badge } from "../../components/ui/badge";

const Field = ({ label, value, testId }) => (
  <div>
    <p className="label-caps">{label}</p>
    <p className="text-sm font-medium text-white mt-1.5" data-testid={testId}>{value || "—"}</p>
  </div>
);

export default function ProfilePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/investor/profile").then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="Profile"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Profile"><PageLoader /></AppLayout>;

  const { profile, kyc_records, kyc_completion, wrm } = data;

  return (
    <AppLayout title="Profile" subtitle="Your account, investment preferences and compliance details.">
      <div className="fade-up grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 glass-card p-7">
          <div className="flex items-center gap-5 mb-8">
            <SecureAvatar investorId={profile.id} name={profile.full_name} size={64} testId="profile-photo" />
            <div>
              <h3 className="font-display text-2xl text-white">{profile.full_name}</h3>
              <p className="tnum text-xs text-zinc-500 mt-1">{profile.investor_code}</p>
            </div>
          </div>
          <h3 className="font-heading font-semibold text-white mb-7">Client Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Full Name" value={profile.full_name} testId="profile-name" />
            <Field label="Client ID" value={profile.investor_code} testId="profile-code" />
            <Field label="Email" value={profile.email} testId="profile-email" />
            <Field label="Mobile" value={profile.mobile} testId="profile-mobile" />
            <Field label="Date of Birth" value={fmtDate(profile.dob)} testId="profile-dob" />
            <Field label="Gender" value={profile.gender} testId="profile-gender" />
            <Field label="City" value={profile.city ? `${profile.city}, ${profile.state}` : null} testId="profile-city" />
            <Field label="Onboarding Date" value={fmtDate(profile.onboarding_date)} testId="profile-onboarding" />
            <Field label="Investment Plan" value={profile.investment_plan} testId="profile-plan" />
            <div>
              <p className="label-caps">Risk Profile</p>
              <Badge variant="outline" className={`${riskBadge(profile.risk_profile)} text-[11px] mt-1.5`} data-testid="profile-risk">
                {profile.risk_profile}
              </Badge>
            </div>
          </div>

          <h3 className="font-heading font-semibold text-white mt-10 mb-6">Nominee</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-6">
            <Field label="Nominee Name" value={profile.nominee?.name} testId="nominee-name" />
            <Field label="Relationship" value={profile.nominee?.relation} testId="nominee-relation" />
            <Field label="Share" value={profile.nominee?.share_pct ? `${profile.nominee.share_pct}%` : "—"} testId="nominee-share" />
          </div>

          <h3 className="font-heading font-semibold text-white mt-10 mb-4">KYC Status</h3>
          <div className="flex flex-wrap items-center gap-4 mb-5">
            <Badge variant="outline" data-testid="kyc-overall-status" className={`text-xs px-3 py-1 ${kycBadge(profile.kyc_status)}`}>
              {profile.kyc_status}
            </Badge>
            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
              <div className="h-1.5 rounded-full bg-white/[0.06] flex-1 overflow-hidden">
                <div className="h-1.5 rounded-full bg-emerald-500" style={{ width: `${kyc_completion ?? 0}%` }} />
              </div>
              <span className="tnum text-xs text-emerald-300 font-semibold" data-testid="kyc-completion">{kyc_completion ?? 0}%</span>
            </div>
          </div>
          <div className="space-y-2.5" data-testid="kyc-records">
            {kyc_records.map((k) => (
              <div key={k.id} className="flex items-center justify-between border border-white/[0.06] bg-white/[0.02] rounded-lg px-4 py-2.5">
                <span className="text-sm font-medium text-zinc-200">{k.document_type}</span>
                <Badge variant="outline" className={`text-[10px] ${kycBadge(k.status)}`}>{k.status}</Badge>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="glass-card p-6" data-testid="wrm-card">
            <p className="label-caps mb-5">Your Wealth Manager</p>
            {wrm ? (
              <>
                <div className="flex items-center gap-4">
                  <img src={wrm.avatar} alt={wrm.name} className="h-14 w-14 rounded-full object-cover border border-white/10" />
                  <div>
                    <p className="font-heading font-semibold text-white">{wrm.name}</p>
                    <p className="text-xs text-zinc-500">{wrm.title}</p>
                  </div>
                </div>
                <div className="mt-5 space-y-2.5">
                  <a href={`mailto:${wrm.email}`} data-testid="wrm-email"
                    className="flex items-center gap-2.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors duration-200">
                    <EnvelopeSimple size={15} /> {wrm.email}
                  </a>
                  <a href={`tel:${wrm.phone}`} data-testid="wrm-phone"
                    className="flex items-center gap-2.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors duration-200">
                    <Phone size={15} /> {wrm.phone}
                  </a>
                </div>
              </>
            ) : (
              <p className="text-sm text-zinc-500">A relationship manager will be assigned shortly.</p>
            )}
          </div>

          <div className="glass-card p-6 border-emerald-500/15">
            <p className="font-heading font-semibold text-white">Need assistance?</p>
            <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
              Reach your relationship manager directly or contact the KMC client desk.
            </p>
            <a href="/support" data-testid="profile-support-link"
              className="inline-block mt-4 text-sm font-semibold text-emerald-400 border border-emerald-500/25 rounded-lg px-4 py-2 hover:bg-emerald-500/10 transition-colors duration-200">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
